import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import PortafoglioPanel from "./PortafoglioPanel";
import { ManualeButton } from "@/components/ManualeButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  BarChart3, Loader2, Settings2, TrendingUp, Building2, Tags, Upload, Download,
  Image as ImageIcon, Users, Package, ListFilter, X, Wallet,
} from "lucide-react";

const MACRO_META = {
  RCA: { label: "RCA", cls: "bg-indigo-100 text-indigo-700 border-indigo-200", color: "#6366f1" },
  RE: { label: "Rami Elementari", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", color: "#10b981" },
  VITA: { label: "Vita", cls: "bg-amber-100 text-amber-700 border-amber-200", color: "#f59e0b" },
};
const NICCHIE = ["R.C. Auto", "Infortuni / Salute", "R.C. Diversi / Professionale", "Multirischi / Danni ai beni", "Tutela Legale", "Furto", "Cauzioni", "Catastrofi Naturali", "Vita", "Altri Rami Elementari"];
const PALETTE = ["#7c3aed", "#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#8b5cf6", "#f97316"];

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const eurShort = (n) => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(0)}k` : `${n}`);

const EMPTY = {
  metric: "lordo", date_basis: "effetto", dfrom: "", dto: "", compagnia: "", produttore: "",
  tipo_operazione: "", modalita: "", ramo: "", macro: "", nicchia: "", prodotto: "", comune: "", targa: "", q: "",
};

export default function DirezioneDashboard() {
  const [f, setF] = useState(EMPTY);
  const [cfg, setCfg] = useState({ compagnie: [], produttori: [], tipi_operazione: [], modalita: [], rami: [], dataset: {}, is_admin: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("compagnia");
  const [source, setSource] = useState("fc");
  const [mapOpen, setMapOpen] = useState(false);
  const [ramoMap, setRamoMap] = useState([]);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);
  const chartsRef = useRef(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const loadCfg = useCallback(async () => {
    try { const { data } = await api.get("/direzione/config"); setCfg(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const params = useCallback(() => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    p.metric = f.metric; p.date_basis = f.date_basis;
    return p;
  }, [f]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/direzione/overview", { params: params() }); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/direzione/import", fd);
      toast.success(`Importate ${data.righe} righe (${data.periodo_da || "?"} → ${data.periodo_a || "?"})`);
      await loadCfg();
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/direzione/export.xlsx", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `produzione_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const doExportPng = async () => {
    if (!chartsRef.current) return;
    try {
      const dataUrl = await toPng(chartsRef.current, { backgroundColor: "#ffffff", pixelRatio: 2, skipFonts: true });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `grafici_${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
    } catch { toast.error("Impossibile generare l'immagine dei grafici"); }
  };

  const openMap = async () => {
    setMapOpen(true);
    try { const { data } = await api.get("/direzione/ramo-map"); setRamoMap(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveMap = async (row, newMacro, newNicchia) => {
    try {
      await api.post("/direzione/ramo-map", { ramo: row.ramo, macro: newMacro, nicchia: newNicchia });
      toast.success(`"${row.ramo}" → ${newMacro} / ${newNicchia}`);
      const { data } = await api.get("/direzione/ramo-map"); setRamoMap(data); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const totale = data?.totale || 0;
  const pct = (v) => (totale > 0 ? Math.round((v / totale) * 100) : 0);
  const activeFilters = Object.entries(f).filter(([k, v]) => v && !["metric", "date_basis"].includes(k)).length;

  const compChart = (data?.by_compagnia || []).slice(0, 8).map((r) => ({ name: r.compagnia, premio: r.premio }));
  const prodChart = (data?.by_produttore || []).slice(0, 8).map((r) => ({ name: r.produttore, premio: r.premio }));
  const macroChart = ["RCA", "RE", "VITA"].map((m) => ({ name: MACRO_META[m].label, value: data?.by_macro?.[m] || 0, color: MACRO_META[m].color }));

  const TABS = [
    { key: "compagnia", label: "Compagnia", icon: Building2, rows: data?.by_compagnia, col: "compagnia" },
    { key: "produttore", label: "Collaboratore", icon: Users, rows: data?.by_produttore, col: "produttore" },
    { key: "prodotto", label: "Prodotto", icon: Package, rows: data?.by_prodotto, col: "prodotto" },
    { key: "ramo", label: "Ramo", icon: Tags, rows: data?.by_ramo, col: "ramo" },
    { key: "tipo", label: "Tipo Operazione", icon: ListFilter, rows: data?.by_tipo_operazione, col: "tipo_operazione" },
  ];
  const activeTab = TABS.find((t) => t.key === tab);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="direzione-dashboard">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center"><BarChart3 className="h-6 w-6" /></div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Dashboard Direzionale</h1>
              <p className="text-sm text-slate-500">
                Produzione dal Foglio Cassa · {cfg.dataset?.righe || 0} righe
                {cfg.dataset?.import?.filename ? ` · ${cfg.dataset.import.filename}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 overflow-hidden mr-1" data-testid="dir-source-switch">
              <button onClick={() => setSource("fc")} className={`px-3 py-2 text-sm font-medium ${source === "fc" ? "bg-navy text-white" : "bg-white text-slate-600"}`} data-testid="dir-source-fc">Foglio Cassa</button>
              <button onClick={() => setSource("pf")} className={`px-3 py-2 text-sm font-medium ${source === "pf" ? "bg-navy text-white" : "bg-white text-slate-600"}`} data-testid="dir-source-pf">Portafoglio</button>
            </div>
            <ManualeButton route="/manuale/dashboard-direzionale" />
            {source === "fc" && (<>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
              onChange={(e) => doImport(e.target.files?.[0])} data-testid="dir-import-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="dir-import-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Importa Foglio Cassa
            </Button>
            <Button variant="outline" onClick={openMap} data-testid="dir-ramo-map-btn"><Settings2 className="h-4 w-4 mr-1.5" /> Mappatura rami</Button>
            </>)}
          </div>
        </div>

        {/* Filtri */}
        {source === "fc" && (<>
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6" data-testid="dir-filters">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Metrica</label>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button onClick={() => set("metric", "lordo")} className={`px-3 py-2 text-sm ${f.metric === "lordo" ? "bg-navy text-white" : "bg-white text-slate-600"}`} data-testid="dir-metric-lordo">Lordo</button>
                <button onClick={() => set("metric", "netto")} className={`px-3 py-2 text-sm ${f.metric === "netto" ? "bg-navy text-white" : "bg-white text-slate-600"}`} data-testid="dir-metric-netto">Netto</button>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Periodo per</label>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button onClick={() => set("date_basis", "effetto")} className={`px-3 py-2 text-sm ${f.date_basis === "effetto" ? "bg-navy text-white" : "bg-white text-slate-600"}`} data-testid="dir-basis-effetto">Effetto</button>
                <button onClick={() => set("date_basis", "incasso")} className={`px-3 py-2 text-sm ${f.date_basis === "incasso" ? "bg-navy text-white" : "bg-white text-slate-600"}`} data-testid="dir-basis-incasso">Incasso</button>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Dal</label>
              <Input type="date" value={f.dfrom} onChange={(e) => set("dfrom", e.target.value)} className="h-10 w-40" data-testid="dir-from" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Al</label>
              <Input type="date" value={f.dto} onChange={(e) => set("dto", e.target.value)} className="h-10 w-40" data-testid="dir-to" />
            </div>
            <FilterSelect label="Compagnia" testid="dir-compagnia" value={f.compagnia} onChange={(v) => set("compagnia", v)} options={cfg.compagnie} allLabel="Tutte le compagnie" w="w-52" />
            <FilterSelect label="Collaboratore" testid="dir-produttore" value={f.produttore} onChange={(v) => set("produttore", v)} options={cfg.produttori} allLabel="Tutti i collaboratori" w="w-48" />
            <div>
              <label className="text-xs text-slate-500 block mb-1">Macro-ramo</label>
              <Select value={f.macro || "all"} onValueChange={(v) => set("macro", v === "all" ? "" : v)}>
                <SelectTrigger className="h-10 w-40" data-testid="dir-macro"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="RCA">RCA</SelectItem>
                  <SelectItem value="RE">Rami Elementari</SelectItem>
                  <SelectItem value="VITA">Vita</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FilterSelect label="Nicchia" testid="dir-nicchia" value={f.nicchia} onChange={(v) => set("nicchia", v)} options={NICCHIE} allLabel="Tutte" w="w-52" />
            <FilterSelect label="Tipo Operazione" testid="dir-tipo" value={f.tipo_operazione} onChange={(v) => set("tipo_operazione", v)} options={cfg.tipi_operazione} allLabel="Tutte le operazioni" w="w-52" />
            <FilterSelect label="Modalità incasso" testid="dir-modalita" value={f.modalita} onChange={(v) => set("modalita", v)} options={cfg.modalita} allLabel="Tutte" w="w-44" />
            <FilterSelect label="Ramo (grezzo)" testid="dir-ramo" value={f.ramo} onChange={(v) => set("ramo", v)} options={cfg.rami} allLabel="Tutti i rami" w="w-44" />
            <div>
              <label className="text-xs text-slate-500 block mb-1">Prodotto</label>
              <Input value={f.prodotto} onChange={(e) => set("prodotto", e.target.value)} placeholder="es. autovett…" className="h-10 w-40" data-testid="dir-prodotto" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Comune</label>
              <Input value={f.comune} onChange={(e) => set("comune", e.target.value)} placeholder="es. Nuoro" className="h-10 w-36" data-testid="dir-comune" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Targa</label>
              <Input value={f.targa} onChange={(e) => set("targa", e.target.value)} placeholder="es. CC046AR" className="h-10 w-32" data-testid="dir-targa" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-slate-500 block mb-1">Ricerca libera</label>
              <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="contraente, polizza, CF, targa…" className="h-10" data-testid="dir-search" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            {activeFilters > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setF({ ...EMPTY, metric: f.metric, date_basis: f.date_basis })} data-testid="dir-reset" className="text-slate-500">
                <X className="h-4 w-4 mr-1" /> Azzera filtri ({activeFilters})
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={doExportPng} data-testid="dir-export-png"><ImageIcon className="h-4 w-4 mr-1.5" /> Grafici PNG</Button>
              <Button size="sm" onClick={doExport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dir-export-xlsx">
                {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Scarica Excel
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-16 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div>
        ) : (
          <>
            {/* KPI */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-5" data-testid="dir-kpi-totale">
                <div className="flex items-center gap-2 text-violet-700 mb-1"><TrendingUp className="h-5 w-5" /><span className="text-sm font-medium">Produzione</span></div>
                <p className="text-2xl font-bold text-slate-900">{eur(totale)}</p>
                <p className="text-xs text-slate-500 mt-1">{data?.titoli || 0} titoli · {data?.metric}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="dir-kpi-provvigioni">
                <div className="flex items-center gap-2 text-slate-600 mb-1"><Wallet className="h-5 w-5" /><span className="text-sm font-medium">Provvigioni</span></div>
                <p className="text-2xl font-bold text-slate-900">{eur(data?.provvigioni)}</p>
              </div>
              {["RCA", "RE", "VITA"].map((m) => (
                <div key={m} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`dir-kpi-${m}`}>
                  <Badge variant="outline" className={MACRO_META[m].cls}>{MACRO_META[m].label}</Badge>
                  <p className="text-xl font-bold text-slate-900 mt-2">{eur(data?.by_macro?.[m])}</p>
                  <p className="text-xs text-slate-500 mt-1">{pct(data?.by_macro?.[m])}% del totale</p>
                </div>
              ))}
            </div>

            {/* Grafici */}
            <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-testid="dir-charts">
              <ChartCard title="Per Compagnia" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={compChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                    <YAxis tickFormatter={eurShort} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Bar dataKey="premio" radius={[6, 6, 0, 0]}>
                      {compChart.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Per Macro-ramo">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={macroChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(e) => eurShort(e.value)}>
                      {macroChart.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Per Collaboratore" className="lg:col-span-3">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={prodChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                    <YAxis tickFormatter={eurShort} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Bar dataKey="premio" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {/* Tabelle a schede */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex flex-wrap border-b border-slate-100" data-testid="dir-table-tabs">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)}
                      className={`flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 -mb-px ${tab === t.key ? "border-violet-600 text-violet-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                      data-testid={`dir-tab-${t.key}`}>
                      <Icon className="h-4 w-4" /> {t.label}
                    </button>
                  );
                })}
              </div>
              <table className="w-full text-sm" data-testid={`dir-table-${tab}`}>
                <thead className="text-xs text-slate-400 bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">{activeTab.label}</th>
                    {tab === "ramo" && <th className="text-left px-4 py-2 font-medium">Macro</th>}
                    <th className="text-right px-4 py-2 font-medium">N.</th>
                    <th className="text-right px-4 py-2 font-medium">Premio {data?.metric}</th>
                    <th className="text-right px-4 py-2 font-medium w-16">%</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeTab.rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-700">{tab === "ramo" ? r.nicchia : (r[activeTab.col] || "—")}</td>
                      {tab === "ramo" && <td className="px-4 py-2.5"><Badge variant="outline" className={MACRO_META[r.macro]?.cls}>{r.macro}</Badge></td>}
                      <td className="px-4 py-2.5 text-right text-slate-400 text-xs">{r.count}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-900">{eur(r.premio)}</td>
                      <td className="px-4 py-2.5 text-right text-xs text-slate-400">{pct(r.premio)}%</td>
                    </tr>
                  ))}
                  {!(activeTab.rows || []).length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nessun dato per i filtri selezionati</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
        </>)}
        {source === "pf" && <PortafoglioPanel />}
      </main>

      {/* Mappatura rami */}
      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="dir-ramo-map-dialog">
          <DialogHeader>
            <DialogTitle>Mappatura rami</DialogTitle>
            <DialogDescription>Correggi la categoria assegnata a un ramo grezzo. Le modifiche si riflettono subito sui KPI.</DialogDescription>
          </DialogHeader>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400"><tr><th className="text-left py-1">Ramo (Foglio Cassa)</th><th className="text-left py-1">Macro</th><th className="text-left py-1">Nicchia</th></tr></thead>
            <tbody>
              {ramoMap.map((r) => (
                <tr key={r.ramo} className="border-t border-slate-50" data-testid={`dir-map-row-${r.ramo}`}>
                  <td className="py-2 pr-2 text-slate-700">{r.ramo} {r.override && <span className="text-[10px] text-violet-600">(modificato)</span>}</td>
                  <td className="py-2 pr-2">
                    <Select value={r.macro} onValueChange={(v) => saveMap(r, v, r.nicchia)}>
                      <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="RCA">RCA</SelectItem><SelectItem value="RE">RE</SelectItem><SelectItem value="VITA">VITA</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="py-2">
                    <Select value={NICCHIE.includes(r.nicchia) ? r.nicchia : "Altri Rami Elementari"} onValueChange={(v) => saveMap(r, r.macro, v)}>
                      <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
                      <SelectContent>{NICCHIE.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <DialogFooter><Button onClick={() => setMapOpen(false)} data-testid="dir-map-close">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, testid, value, onChange, options, allLabel, w }) {
  return (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className={`h-10 ${w}`} data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="all">{allLabel}</SelectItem>
          {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
      <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3">{title}</h3>
      {children}
    </div>
  );
}
